import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { TriangleAlert } from 'lucide';
import { NbIcon } from '../../icon/icon';

/**
 * `<nb-field>` — labelled form-field chrome (2026-09 prototype: label 12.5/700
 * above the control, optional leading icon inside the control in `--faint`).
 *
 * Wraps a projected control (use `nbInput`) with a label, optional help text,
 * and a clear-language error message. The error pairs an icon with the text so
 * the failure is never signalled by colour alone (WCAG 1.4.1). Link the control
 * with `controlId` so the label's `for` resolves. Project an `<nb-icon nbFieldPrefix>`
 * to render a leading icon inside the control (the input gets extra left padding).
 *
 * @example
 * <nb-field label="E-post" controlId="epost" hint="Vi deler den aldri.">
 *   <nb-icon nbFieldPrefix [icon]="Mail" />
 *   <input nbInput id="epost" type="email" />
 * </nb-field>
 */
@Component({
  selector: 'nb-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  template: `<div class="field">
    @if (label()) {
      <label [attr.for]="controlId() || null">{{ label() }}</label>
    }
    <div class="control">
      <ng-content select="[nbFieldPrefix]" />
      <ng-content />
    </div>
    @if (error()) {
      <p class="nb-field-msg nb-field-error" role="alert">
        <nb-icon [icon]="TriangleAlert" [size]="15" />{{ error() }}
      </p>
    } @else if (hint()) {
      <p class="nb-field-msg nb-field-hint">{{ hint() }}</p>
    }
  </div>`,
  styleUrl: './field.css',
})
export class NbField {
  readonly label = input<string>();
  readonly controlId = input<string>();
  readonly hint = input<string>();
  readonly error = input<string>();

  protected readonly TriangleAlert = TriangleAlert;
}
